import React, { useState } from 'react';
import { Flex, Box, Text } from 'rebass/styled-components';
import { Button, Popover, ProfilePicture } from '@getro/rombo';
import PropTypes from 'prop-types';
import { ContactMoreActions } from 'components/organisms/contactActionPanel/contactMoreActions';
import { AlertTriangle, MoreHorizontal } from 'lucide-react';
import ContentLoader from 'react-content-loader';
import { useTheme } from 'styled-components';
import { RefreshContactModal } from 'components/molecules/refreshContactModal';
import { trackEvent } from 'helpers/analytics';
import { getContactDisplayName } from 'helpers/getContactDisplayName';
import AddContactsToList from 'components/organisms/contactCard/addContactToList';
import { RequestIntro } from 'components/organisms/contactActionPanel/requestIntro';
import { useHistory } from 'react-router-dom';

export const ContactNameRenderer = ({ data, ...props }) => {
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const { colors } = useTheme();
  const contact = data.original;
  const history = useHistory();

  if (data.options.showLoader) {
    return (
      <ContentLoader
        speed={2}
        width={400}
        height={120}
        viewBox="0 0 400 120"
        backgroundColor={colors.neutral[50]}
        foregroundColor={colors.neutral[20]}
        {...props}
      >
        <rect x="47" y="20" rx="4" ry="4" width="80" height="8" />
        <circle cx="23" cy="25" r="13" />
        <rect x="138" y="19" rx="4" ry="4" width="40" height="8" />
      </ContentLoader>
    );
  }

  const { options } = data;
  const contactInList =
    data?.options?.list?.id && contact?.sharedList?.some((item) => item.id === parseInt(data.options.list.id, 10));

  const notEnriched = contact.enrichmentStatus !== 'enriched';

  return (
    <Flex
      width="100%"
      sx={{
        gap: '8px',
        justifyContent: 'space-between',
        width: '100%',
        alignItems: 'center',
        pr: '8px',
        height: '100%',
        pl: '8px',
      }}
    >
      <Box
        onClick={() => {
          if (options.type !== 'public') {
            history.push(options.contactUrl);
          }
        }}
        sx={{
          gap: '8px',
          display: 'flex',
          cursor: options.type === 'public' ? 'default' : 'pointer',
          alignItems: 'center',
          flexGrow: 1,
          textDecoration: 'none',
          width: '100%',
        }}
      >
        <Box width="24px" height="24px" flex="0 0 auto">
          <ProfilePicture
            sx={{
              minWidth: '24px',
              minHeight: '24px',
            }}
            imageUrl={contact.profilePicUrl || ''}
            name={getContactDisplayName(contact, 'Getro')}
          />
        </Box>
        <Text
          fontSize="14px"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: 'text.main',
          }}
        >
          {getContactDisplayName(contact)}
        </Text>
        {notEnriched && !showRefreshModal && data.options.type !== 'public' && (
          <Popover
            interactive
            position="right-start"
            content={
              <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
                <Text color="text.dark" fontSize="14px" fontWeight="600">
                  We couldn’t enrich this contact automatically
                </Text>
                <Text color="text.main" fontSize="14px">
                  The LinkedIn profile may be private or invalid.
                </Text>
                <Box>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      setShowRefreshModal(true);
                    }}
                  >
                    Learn more
                  </Button>
                </Box>
              </Flex>
            }
            trigger={
              <Box sx={{ minWidth: '16px', minHeight: '16px', pt: '6px' }}>
                <Box as={AlertTriangle} sx={{ color: 'warning' }} size={16} aria-hidden="true" />
              </Box>
            }
          />
        )}
      </Box>
      {data.options.type === 'public' && data.options.list.shareableView?.introRequestsEnabled && (
        <Box sx={{ maxWidth: '140px', width: '100%' }}>
          <RequestIntro list={data.options.list} selectedContacts={[contact]} slug={options.activeNetwork.slug} />
        </Box>
      )}
      {showRefreshModal && (
        <RefreshContactModal
          onCloseModal={() => setShowRefreshModal(false)}
          linkedinUrl={contact.linkedinUrl}
          trackRefreshEvent={() => {
            trackEvent('contacts:refresh_profile_click', {
              collectionId: data.options.activeNetwork.id,
              contactId: contact.id,
              linkedinHandle: contact?.linkedinHandle,
              page: data.options.listId ? 'list' : 'contacts',
              informationDaysOld: contact.enrichedProfileAgeInDays,
              experienceDaysOld: contact.enrichedExperienceAgeInDays,
              contactEnriched: contact.enrichmentStatus === 'enriched',
              experiencesCount: contact.contactWorkExperiences.length,
            });
          }}
        />
      )}

      {(options.type !== 'public' || options.page === 'list:add') && (
        <Flex width="fit-content" sx={{ justifyContent: 'flex-end', alignItems: 'center', gap: '8px' }}>
          {options.page === 'list:add' && (
            <AddContactsToList
              page={options.page}
              queryParams={options.queryParams}
              contact={data.original}
              list={data.options.list}
              fullContactView={false}
              view="table"
              buttonProps={{
                variant: contactInList ? 'tertiary' : 'accent',
                size: 'small',
                minWidth: 'fit-content',
              }}
            />
          )}
          {options.type !== 'public' && (
            <Box sx={{ minWidth: '24px' }}>
              <ContactMoreActions
                page={options.page}
                view="table"
                queryParams={options.queryParams}
                showMoreOptions
                contact={data.original}
                deleteContactFromList={options.deleteContactFromList}
                list={options.list}
                fullContactView={false}
                triggerIcon={MoreHorizontal}
                sx={{
                  height: 'fit-content',
                  width: 'fit-content',
                  minWidth: '24px',
                }}
              />
            </Box>
          )}
        </Flex>
      )}
    </Flex>
  );
};

ContactNameRenderer.propTypes = {
  data: PropTypes.object,
  context: PropTypes.object,
};

ContactNameRenderer.defaultProps = {
  data: {},
  context: {},
};
